// PricingSection.jsx
import React, { useState, useEffect } from "react";
import "./PricingSection.css";
import { useQuota } from "../../context/QuotaContext";
import CheckoutModal from "../CheckoutModal/CheckoutModal";

const PricingCard = ({
  basePrice,
  title,
  desc,
  features,
  btnText,
  isMostPopular,
  badgeColor,
  planKey,
  status,
  handleCheckout,
  yearly,
}) => {
  const getPrice = () => (yearly ? `$${(basePrice / 2) - 0.5}` : `$${basePrice}`);
  const isDisabled = status !== "available";

  return (
    <div
      className={`card-wrapper ${status === "locked" ? "locked" : ""}`}
      style={status === "locked" ? { opacity: 0.6, pointerEvents: "none" } : {}}
    >
      {isMostPopular && (
        <div className="badge" style={{ backgroundColor: badgeColor }}>
          MOST POPULAR
        </div>
      )}

      <div className={`card ${isMostPopular ? "most-popular" : ""}`}>
        <h2 className="price">
          {getPrice()}
          <span>/mo</span>
        </h2>

        <h3 className="plan-name">{title}</h3>
        <p className="plan-desc">{desc}</p>

        <ul className="features">
          {features.map((f, i) => (
            <li key={i} className={f.type}>
              {f.text}
            </li>
          ))}
        </ul>

        <button
          className="cta-btn"
          onClick={() => handleCheckout(planKey, yearly)}
          disabled={isDisabled}
        >
          {status === "current" ? "Current Plan" : btnText}
        </button>
      </div>
    </div>
  );
};

const PricingSection = () => {
  const { quota, fetchQuota } = useQuota();
  const [modalData, setModalData] = useState(null); // { planKey, yearly }
  const [yearly, setYearly] = useState(true);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const handleCheckout = async (planKey, yearly = true, billing = null) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;
      const LOGIN_URL = `${FRONTEND_URL}/login`;

      const meRes = await fetch(`${API_URL}/auth/me`, { credentials: "include" });
      if (!meRes.ok) {
        window.location.href = LOGIN_URL;
        return;
      }

      if (!billing) {
        setModalData({ planKey, yearly });
        return;
      }

      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_name: planKey,
          duration: yearly ? "yearly" : "monthly",
          success_url: `${FRONTEND_URL}/success`,
          cancel_url: `${FRONTEND_URL}/cancel`,
          billing,
        }),
      });

      if (!res.ok) {
        let errorMsg = "Checkout failed";
        try {
          const errorData = await res.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (_) {}
        throw new Error(errorMsg);
      }

      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert("Failed to start checkout");
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Something went wrong. Please try again.");
    }
  };

  const userPlan = quota?.plan_name?.split("-")[0];
  const getPlanStatus = (planKey) => {
    const planOrder = ["starter", "creator", "pro"];
    if (!userPlan) return "available";
    if (planKey === userPlan) return "current";
    const userIndex = planOrder.indexOf(userPlan);
    const planIndex = planOrder.indexOf(planKey);
    return planIndex < userIndex ? "locked" : "available";
  };

  return (
    <section className="pricing-section">
      <h1 className="pricing-title">
        Turn Your LinkedIn Presence
        <br /> Into A{" "}
        <span className="highlight">Personal Brand Engine.</span>
      </h1>
      <p className="pricing-subtitle">
        Start <strong>Free</strong>. Cancel <strong>Anytime</strong>. <br />
        No Credit Card Needed.
      </p>

      {/* 🌍 Universal Billing Toggle */}
      <div className="universal-toggle">
        <span className={`toggle-label ${!yearly ? "active" : ""}`}>Monthly</span>
        <div
          className={`toggle-switch ${yearly ? "active" : ""}`}
          onClick={() => setYearly((v) => !v)}
          role="button"
          aria-pressed={yearly}
        >
          <div className="toggle-knob" />
        </div>
        <span className={`toggle-label ${yearly ? "active" : ""}`}>Yearly</span>
      </div>

      <div className="pricing-cards">
        <PricingCard
          basePrice={29}
          title="Starter"
          planKey="starter"
          desc="For creators starting to take LinkedIn seriously."
          btnText="Get Started Now!"
          features={[
            { text: "30 Post Generations", type: "check" },
            { text: "30 Idea Generations", type: "check" },
            { text: "30 Hooks Generations", type: "check" },
            { text: "Post Scheduling", type: "check" },
            { text: "Carousel Generator (with Watermark)", type: "check" },
            { text: "Summary Generator", type: "check" },
            { text: "Headlines Generator", type: "check" },
          ]}
          status={getPlanStatus("starter")}
          handleCheckout={handleCheckout}
          yearly={yearly}
        />

        <PricingCard
          basePrice={49}
          title="Creator"
          planKey="creator"
          desc="For creators scaling content & audience-building faster."
          btnText="Scale up later!"
          isMostPopular
          badgeColor="#26B170"
          features={[
            { text: "100 Post Generations", type: "check" },
            { text: "100 Idea Generations", type: "check" },
            { text: "100 Hook Generations", type: "check" },
            { text: "Everything in Starter", type: "check" },
            { text: "No Watermarks", type: "plus" },
          ]}
          status={getPlanStatus("creator")}
          handleCheckout={handleCheckout}
          yearly={yearly}
        />

        <PricingCard
          basePrice={129}
          title="Pro"
          planKey="pro"
          desc="Built for teams, ghostwriters, and personal branding agencies."
          btnText="Tailored for teams!"
          features={[
            { text: "Everything in Creator – Unlimited", type: "check" },
            { text: "3 Added Users", type: "plus" },
            { text: "Connect & Post from Company Pages", type: "plus" },
            {
              text: "Relationship building through 1000 comments a month",
              type: "plus",
            },
          ]}
          status={getPlanStatus("pro")}
          handleCheckout={handleCheckout}
          yearly={yearly}
        />
      </div>

      {modalData && (
        <CheckoutModal
          isOpen={!!modalData}
          onClose={() => setModalData(null)}
          planKey={modalData.planKey}
          yearly={modalData.yearly}
          handleCheckout={handleCheckout}
        />
      )}
    </section>
  );
};

export default PricingSection;
